/**
 * `isoToDate` / `dateToIso` — the pair that moves a calendar date between the
 * `YYYY-MM-DD` the contract carries and the `Date` a date picker wants.
 *
 * Both work in LOCAL time, deliberately and together. The tempting
 * `new Date("2017-05-03")` parses as UTC midnight; reading it back with the
 * local getters a picker uses then lands on May 2 for anyone west of
 * Greenwich. A calendar date has no time zone — it must not acquire one on the
 * way through a form.
 */

/** `YYYY-MM-DD` → local-midnight Date. `undefined` for blank or malformed input. */
export function isoToDate(value: string | undefined | null): Date | undefined {
  const parts = value?.split("-");
  if (parts === undefined || parts.length !== 3) return undefined;
  const [y, m, d] = parts.map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  const parsed = new Date(y, m - 1, d);
  // The constructor maps years 0-99 onto 1900-1999.
  if (y < 100) parsed.setFullYear(y);
  // It also treats out-of-range components as arithmetic rather than as an
  // error — month 13 rolls to next January, day 32 to the 1st — so an
  // impossible date would come back as a confident wrong one. Reading the
  // components back is what rejects it.
  const valid = parsed.getFullYear() === y && parsed.getMonth() === m - 1 && parsed.getDate() === d;
  return valid ? parsed : undefined;
}

/** Date → `YYYY-MM-DD`, read with the same local getters `isoToDate` writes. */
export function dateToIso(value: Date | undefined): string {
  if (!value || Number.isNaN(value.getTime())) return "";
  const yyyy = value.getFullYear().toString().padStart(4, "0");
  const mm = (value.getMonth() + 1).toString().padStart(2, "0");
  const dd = value.getDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
